import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, X, FileJson, Info, Plus, Trash2, Map, CheckCircle2, Loader2 } from 'lucide-react';
import { baseurl } from '../../baseurl';

const AddZones = ({ submissionId, onUploadSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null); // Track specific ID being deleted
  const [existingZones, setExistingZones] = useState([]);
  
  // Feedback messages
  const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });

  // Form states
  const [zoneName, setZoneName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const adminToken = localStorage.getItem('adminToken');

  // Clear message after 4 seconds
  useEffect(() => {
    if (statusMsg.text) {
      const timer = setTimeout(() => setStatusMsg({ type: '', text: '' }), 4000);
      return () => clearTimeout(timer);
    }
  }, [statusMsg]);

  useEffect(() => {
    if (isOpen) {
      fetchExistingZones();
    }
  }, [isOpen]);

  const fetchExistingZones = async () => {
    try {
      const res = await axios.get(`${baseurl}getProjectZones/${submissionId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (res.data.success) setExistingZones(res.data.data);
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to fetch existing zones.' });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !file.name.endsWith('.json') && !file.name.endsWith('.geojson')) {
      setStatusMsg({ type: 'error', text: 'Please select a valid GeoJSON file.' });
      return;
    }
    setSelectedFile(file);
    setStatusMsg({ type: '', text: '' });
  };

  const handleUpload = async () => {
    if (!zoneName || !selectedFile) {
      setStatusMsg({ type: 'error', text: 'Name and file are required.' });
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('submissionId', submissionId);
    formData.append('zoneName', zoneName);
    formData.append('zoneFile', selectedFile);

    try {
      const res = await axios.post(`${baseurl}uploadProjectZone`, formData, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data.success) {
        setStatusMsg({ type: 'success', text: 'Zone added successfully!' });
        setZoneName('');
        setSelectedFile(null);
        fetchExistingZones();
        if (onUploadSuccess) onUploadSuccess();
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Upload failed. Please try again.' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (zoneId) => {
    setIsDeleting(zoneId);
    try {
      const res = await axios.delete(`${baseurl}deleteZone/${zoneId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      
      if (res.data.success) {
        setStatusMsg({ type: 'success', text: 'Zone deleted.' });
        fetchExistingZones();
        if (onUploadSuccess) onUploadSuccess();
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Could not delete zone.' });
    } finally {
      setIsDeleting(null);
    }
  };

  return (
    <>
      <button onClick={() => setIsOpen(true)} className="btn btn-primary rounded-pill px-4 shadow me-2 d-flex align-items-center">
        <Map size={18} className="me-2" /> Manage Zones
      </button>

      {isOpen && (
        <div className="ZM-modal-overlay">
          <div className="ZM-modal-container ZM-wide">
            <div className="ZM-modal-header">
              <div>
                <h5 className="ZM-modal-title text-primary">Project Zone Management</h5>
                <p className="ZM-modal-subtitle">Upload and manage multiple area zones</p>
              </div>
              <button className="ZM-close-btn" onClick={() => setIsOpen(false)}><X size={20} /></button>
            </div>

            {/* In-Modal Status Message */}
            {statusMsg.text && (
              <div className={`ZM-status-bar ${statusMsg.type}`}>
                {statusMsg.type === 'success' ? <CheckCircle2 size={16} className="me-2" /> : <Info size={16} className="me-2" />}
                {statusMsg.text}
              </div>
            )}

            <div className="ZM-modal-body row g-0">
              <div className="col-md-5 p-4 border-end">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <Plus size={18} className="me-2 text-primary" /> Add New Zone
                </h6>
                
                <div className="mb-3">
                  <label className="small fw-bold text-muted mb-1">ZONE NAME</label>
                  <input 
                    type="text" 
                    className="form-control rounded-3 border-2" 
                    placeholder="e.g. Residential Area A" 
                    value={zoneName}
                    onChange={(e) => setZoneName(e.target.value)}
                  />
                </div>

                <div className="ZM-upload-area small-padding">
                  <input type="file" id="zoneFileUpload" hidden accept=".json,.geojson" onChange={handleFileChange} />
                  <label htmlFor="zoneFileUpload" className="ZM-upload-label pointer">
                    <FileJson size={24} className={selectedFile ? "text-success" : "text-primary"} />
                    <span className="mt-2 text-center fw-bold small">
                      {selectedFile ? selectedFile.name : "Select GeoJSON"}
                    </span>
                  </label>
                </div>

                <button 
                  className="btn btn-primary w-100 rounded-pill mt-3 shadow-sm d-flex align-items-center justify-content-center"
                  onClick={handleUpload}
                  disabled={isUploading || !selectedFile || !zoneName}
                >
                  {isUploading ? <Loader2 size={18} className="animate-spin me-2" /> : <Upload size={18} className="me-2" />}
                  {isUploading ? 'Uploading...' : 'Upload Zone'}
                </button>
              </div>

              <div className="col-md-7 p-4 bg-light-soft">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <CheckCircle2 size={18} className="me-2 text-success" /> Active Zones ({existingZones.length})
                </h6>
                
                <div className="ZM-zone-list">
                  {existingZones.length > 0 ? existingZones.map((zone, idx) => (
                    <div key={idx} className="ZM-zone-card shadow-sm border">
                      <div className="d-flex align-items-center">
                        <div className="ZM-icon-circle me-3"><Map size={16} /></div>
                        <div className="flex-grow-1">
                          <div className="fw-bold small">{zone.zone_name}</div>
                          <div className="text-muted" style={{ fontSize: '10px' }}>{zone.file_path.split('/').pop()}</div>
                        </div>
                        <button 
                            onClick={() => handleDelete(zone.id)} 
                            className="btn btn-link text-danger p-0 ms-2"
                            disabled={isDeleting === zone.id}
                        >
                          {isDeleting === zone.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </button>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-5 text-muted opacity-50">
                      <Info size={30} className="mb-2" />
                      <p className="small">No zones uploaded yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ZM-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(27, 37, 89, 0.6); backdrop-filter: blur(6px); display: flex; align-items: center; justify-content: center; z-index: 2000; }
        .ZM-modal-container { background: white; border-radius: 28px; overflow: hidden; animation: ZM-slideUp 0.3s ease-out; box-shadow: 0 30px 60px -12px rgba(0,0,0,0.25); position: relative; }
        .ZM-wide { width: 95%; max-width: 850px; }
        .ZM-modal-header { padding: 20px 24px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f0f0f0; }
        .ZM-status-bar { padding: 10px 24px; font-size: 13px; font-weight: 600; display: flex; align-items: center; animation: fadeIn 0.3s ease; }
        .ZM-status-bar.success { background: #DCFCE7; color: #15803d; }
        .ZM-status-bar.error { background: #FEE2E2; color: #b91c1c; }
        .ZM-modal-title { font-weight: 800; margin: 0; }
        .ZM-modal-subtitle { font-size: 13px; color: #A3AED0; margin: 0; }
        .ZM-close-btn { background: #F4F7FE; border: none; border-radius: 12px; padding: 8px; }
        .ZM-upload-area { border: 2px dashed #E0E5F2; border-radius: 16px; padding: 20px; text-align: center; background: #FBFCFF; }
        .ZM-upload-label { cursor: pointer; display: flex; flex-direction: column; align-items: center; }
        .ZM-zone-list { max-height: 350px; overflow-y: auto; padding-right: 5px; }
        .ZM-zone-card { background: white; border-radius: 16px; padding: 12px 16px; margin-bottom: 12px; transition: transform 0.2s; }
        .ZM-icon-circle { width: 36px; height: 36px; background: #F4F7FE; color: #4318FF; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .bg-light-soft { background-color: #f8faff; }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ZM-slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </>
  );
};

export default AddZones;